import React, { useEffect, useState } from "react";
import "./WorkReviewPage.css";
import * as XLSX from "xlsx";
import { Link } from "react-router-dom";
import { handleExportVerbatimConfiguration } from "../utils/ProjectUtils";
import {
  loadVerbatimsFromExcel,
  loadVerbatimsFromRadarlyExcel,
  prepareRadarlyExcelImport,
  //exportVerbatimsToXLSX as exportToExcel,
  exportVerbatimsToExcelJS as exportToExcel

} from "../utils/ExcelUtils";

import {
  extractDomainName,
} from "../utils/URLUtils";

import PrimaryButton from "../components/buttons/PrimaryButton";
import SecondaryButton from "../components/buttons/SecondaryButton";
import DeleteButton from "../components/buttons/DeleteButton";
import VerbatimFilter from "../components/VerbatimFilter"; // Import the filter function
import LabelChip from "../components/LabelChip";
import FilterBar from "../components/FilterBar";
import IconButton from "../components/buttons/IconButton";
import LoadModal from "../modals/LoadModal";
import ExportVerbatimsModal from '../modals/ExportVerbatimsModal';
import LabelTypeExportSelectorModal from '../modals/LabelTypeExportSelectorModal';
import ImageModal from '../modals/ImageModal';
import { useVerbatim } from "../context/VerbatimContext";
import { useSetup } from "../context/SetupContext";
import { useNavigate } from "react-router-dom";
import { isBase64Image, base64ToImage, migrateVerbatimFormat, renderVerbatimTranslation, getVerbatimDefectInfo } from '../utils/VerbatimUtils';
import { stripDefectiveVerbatims, validateImportedProjectStructure } from "../utils/ImportValidationUtils";

const mergeImportedLabels = (currentLabels, importedLabels) => {
  const mergedLabels = Object.entries(currentLabels || {}).reduce((acc, [labelType, definitions]) => {
    acc[labelType] = Array.isArray(definitions)
      ? definitions.map((definition) => ({ ...definition }))
      : [];
    return acc;
  }, {});

  Object.entries(importedLabels || {}).forEach(([labelType, definitions]) => {
    if (!mergedLabels[labelType]) {
      mergedLabels[labelType] = [];
    }

    (Array.isArray(definitions) ? definitions : []).forEach((definition) => {
      if (!mergedLabels[labelType].some((currentDefinition) => currentDefinition.name === definition.name)) {
        mergedLabels[labelType].push({ ...definition });
      }
    });
  });

  return mergedLabels;
};

const WorkReviewPage = () => {
  const { getVerbatims, setVerbatims, setVerbatimToEdit, toggleUsedStatus } = useVerbatim();
  const { labels, setLabels, labelTypes, setLabelTypes } = useSetup();

  // States
  const [storedVerbatims, setStoredVerbatims] = useState([]);
  const [filteredVerbatims, setFilteredVerbatims] = useState([]);
  const [filters, setFilters] = useState({
    source: "",
    dynamicFilters: Object.keys(labelTypes).reduce((acc, labelType) => {
      acc[labelType] = [];
      return acc;
    }, {}),
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [showOnlyDefective, setShowOnlyDefective] = useState(false);
  const [resetFilters, setResetFilters] = useState(false);
  const [isCollapsibleOpen, setIsCollapsibleOpen] = useState(false);
  const [csvFileData, setCsvFileData] = useState(null);
  const [csvColumns, setCsvColumns] = useState([]);
  const [radarlyImportOptions, setRadarlyImportOptions] = useState(null);
  const [verbatimsPerPage, setVerbatimsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadModalOpen, setLoadModalOpen] = useState(false);
  const [loadModalMode, setLoadModalMode] = useState('standard');
  const [pendingExcelImport, setPendingExcelImport] = useState(null);
  const [pendingExcelValidation, setPendingExcelValidation] = useState(null);
  const [pendingLoadOption, setPendingLoadOption] = useState(null);
  const [isExportModalOpen, setExportModalOpen] = useState(false);
  const [bubbleState, setBubbleState] = useState({});
  const [translatedBubbleState, setTranslatedBubbleState] = useState({});
  const [bubbleImageState, setBubbleImageState] = useState(false);
  const [modalImage, setModalImage] = useState(null);
  const [modalExportToTxt, setModalExportToTxt] = useState(false);
  const navigate = useNavigate();

  // Handlers
  const handleOpenModal = () => setExportModalOpen(true);
  const handleCloseModal = () => setExportModalOpen(false);
  const handleExportToTxt = (selectedLabelTypes) => {
    handleExportVerbatimConfiguration({ verbatims: filteredVerbatims, selectedLabelTypes });
    setModalExportToTxt(false);
  }

  const closeExcelImportFlow = () => {
    setLoadModalOpen(false);
    setLoadModalMode('standard');
    setCsvFileData(null);
    setCsvColumns([]);
    setRadarlyImportOptions(null);
    setPendingExcelImport(null);
    setPendingExcelValidation(null);
    setPendingLoadOption(null);
  };

  const commitPendingExcelImport = (removeDefective = false) => {
    const validation = pendingExcelValidation || (pendingExcelImport ? validateImportedProjectStructure(pendingExcelImport) : null);
    if (!validation || !pendingLoadOption) return;

    const nextData = removeDefective && validation.defectiveVerbatimIndexes.length > 0
      ? stripDefectiveVerbatims(validation.normalizedData, validation.defectiveVerbatimIndexes)
      : validation.normalizedData;

    const nextVerbatims = Array.isArray(nextData.verbatims) ? [...nextData.verbatims] : [];

    if (pendingLoadOption === "append") {
      const existingIds = storedVerbatims.map((verbatim) => verbatim.id);
      const maxExistingId = existingIds.length > 0 ? Math.max(...existingIds) : 0;
      const updatedNewVerbatims = nextVerbatims.map((verbatim, index) => ({
        ...verbatim,
        id: maxExistingId + index + 1,
      }));

      setVerbatims([...storedVerbatims, ...updatedNewVerbatims]);
      setStoredVerbatims([...storedVerbatims, ...updatedNewVerbatims]);
      setFilteredVerbatims([...storedVerbatims, ...updatedNewVerbatims]);
      setLabels((prevLabels) => mergeImportedLabels(prevLabels, nextData.labels));
      setLabelTypes((prevLabelTypes) => ({ ...prevLabelTypes, ...(nextData.labelTypes || {}) }));
    } else if (pendingLoadOption === "overwrite") {
      const isSequential = nextVerbatims
        .map((item) => item.id)
        .sort((a, b) => a - b)
        .every((id, index) => id === index + 1);

      const normalizedVerbatims = isSequential
        ? nextVerbatims
        : nextVerbatims.map((item, index) => ({ ...item, id: index + 1 }));

      setVerbatims(normalizedVerbatims);
      setStoredVerbatims(normalizedVerbatims);
      setFilteredVerbatims(normalizedVerbatims);
      setLabels(nextData.labels || {});
      setLabelTypes(nextData.labelTypes || {});
    }

    closeExcelImportFlow();
  };

  // Load verbatims from context on mount
  useEffect(() => {
    const verbatims = getVerbatims();
    setStoredVerbatims(verbatims);
  }, [getVerbatims]);

  // Filter and sort verbatims based on current filters and search query
  useEffect(() => {
    if (!Array.isArray(storedVerbatims) || storedVerbatims.length === 0) {
      setFilteredVerbatims([]);
      return;
    }

    // Sort verbatims by ID in descending order
    const sortedVerbatims = [...storedVerbatims].sort(
      (a, b) => (b.id ?? 0) - (a.id ?? 0)
    );

    // Apply filters
    let filtered = VerbatimFilter(sortedVerbatims, filters);

    // Apply text search filter
    if (searchQuery.trim() !== "") {
      filtered = filtered.filter(
        (verbatim) =>
          verbatim.verbatim &&
          (() => {
            const verbatimText = typeof verbatim.verbatim === "string"
              ? verbatim.verbatim
              : Array.isArray(verbatim.verbatim)
                ? verbatim.verbatim
                  .filter((item) => item?.type === "text" && typeof item.content === "string")
                  .map((item) => item.content)
                  .join(" ")
                : "";

            return verbatimText.toLowerCase().includes(searchQuery.toLowerCase());
          })()
      );
    }

    if (showOnlyDefective) {
      filtered = filtered.filter((verbatim) => getVerbatimDefectInfo(verbatim).isDefective);
    }

    setFilteredVerbatims(filtered);
    setCurrentPage(1); // Reset to the first page when verbatims change
  }, [storedVerbatims, filters, searchQuery, showOnlyDefective]);

  /************************************************
   * FILE HANDLING FUNCTIONS
   ************************************************/

  // Handle Excel file selection by the user
  const handleRadarlyExcelFileSelect = (event) => {
    const file = event.target.files[0]; // Get the selected file
    if (file) {
      const reader = new FileReader(); // Create a new FileReader to read the file
      reader.onload = (e) => {
        try {
          const binaryStr = e.target.result;
          const workbook = XLSX.read(binaryStr, { type: "binary" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = sheetName ? workbook.Sheets[sheetName] : null;
          const preparedImport = prepareRadarlyExcelImport(worksheet);

          setCsvColumns(preparedImport.columns);
          setCsvFileData(preparedImport.excelData);
          setRadarlyImportOptions({
            format: preparedImport.format,
            headerRowNumber: preparedImport.headerRowNumber,
          });
          setPendingExcelImport(null);
          setPendingExcelValidation(null);
          setPendingLoadOption(null);
          setLoadModalMode('radarly');
          setLoadModalOpen(true);
        } catch (error) {
          alert(`The Radarly Excel file could not be read: ${error.message}`);
        }
      };
      reader.onerror = () => alert('The Radarly Excel file could not be read.');
      reader.readAsBinaryString(file);
    }
  };

  // Handle the user's column selection from the Radarly file
  const handleRadarlyColumnsConfirm = (selectedColumns) => {
    if (!radarlyImportOptions) return;

    const {
      verbatims: newVerbatims,
      labels: newLabelsWithTypes,
      labelTypes: newLabelTypes,
      sourceRows,
    } = loadVerbatimsFromRadarlyExcel(
      csvFileData,
      selectedColumns,
      radarlyImportOptions
    );

    if (!newVerbatims) return;

    const importedProject = {
      verbatims: Array.isArray(newVerbatims) ? newVerbatims : [],
      labels: newLabelsWithTypes || {},
      labelTypes: newLabelTypes || {},
    };

    const validation = validateImportedProjectStructure(importedProject, {
      sourceRows,
      sourceType: 'row',
    });

    setPendingExcelImport(validation.normalizedData);
    setPendingExcelValidation(validation);
  };

  // Handle exporting the current verbatims, labels, and label types to an Excel file
  const handleExportToExcel = (proceed) => {
    setExportModalOpen(false);
    if (proceed) {
      exportToExcel(storedVerbatims, labels, labelTypes);
    } else {
      console.log('Export canceled due to issues.');
    }
  };

  // Handle changes in filter options
  const handleFilterChange = (newFilters) => {
    console.log("Filters updated:", newFilters);
    setFilters(newFilters); // Update the filters state with the new filter options
  };

  // Handle text-based search input change
  const handleSearchQueryChange = (event) => {
    setSearchQuery(event.target.value); // Update the search query state
  };

  // Handle file selection when the user uploads an Excel file
  const handleFileSelect = (event) => {
    const file = event.target.files[0]; // Get the selected file
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const {
            verbatims: newVerbatims,
            labels: newLabels,
            labelTypes: newLabelTypes,
            sourceRows,
          } = loadVerbatimsFromExcel(data);
          const importedProject = {
            verbatims: Array.isArray(newVerbatims) ? newVerbatims : [],
            labels: newLabels || {},
            labelTypes: newLabelTypes || {},
          };
          const validation = validateImportedProjectStructure(importedProject, {
            sourceRows,
            sourceType: 'row',
          });

          setPendingExcelImport(validation.normalizedData);
          setPendingExcelValidation(validation);
          setPendingLoadOption(null);
          setLoadModalMode('standard');
          setLoadModalOpen(true);
        } catch (error) {
          alert(`The Excel file could not be read: ${error.message}`);
        }
      };
      reader.onerror = () => alert('The Excel file could not be read.');
      reader.readAsArrayBuffer(file);
    }
  };

  // Handle the option selected from the modal (append or overwrite verbatims)
  const handleLoadOptionSelect = (option) => {
    setPendingLoadOption(option);
  };

  const handleValidationContinueAll = () => {
    commitPendingExcelImport(false);
  };

  const handleValidationContinueWithoutDefective = () => {
    commitPendingExcelImport(true);
  };

  /************************************************
   * PAGINATION FUNCTIONS
   ************************************************/

  // Handle the change in the number of verbatims displayed per page
  const handleVerbatimsPerPageChange = (event) => {
    setVerbatimsPerPage(Number(event.target.value)); // Update the state with the new number of verbatims per page
    setCurrentPage(1); // Reset to the first page when changing the number of verbatims per page
  };

  // Handle page changes for pagination
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage); // Update the current page number
  };

  // Ensure all the values are defined and valid
  const safeFilteredVerbatims = Array.isArray(filteredVerbatims)
    ? filteredVerbatims
    : []; // Ensure it's an array
  const safeCurrentPage =
    Number.isInteger(currentPage) && currentPage > 0 ? currentPage : 1; // Default to page 1 if invalid
  const safeVerbatimsPerPage =
    Number.isInteger(verbatimsPerPage) && verbatimsPerPage > 0
      ? verbatimsPerPage
      : 10; // Default to 10 per page if invalid

  // Get the paginated verbatims to display on the current page
  const paginatedVerbatims = safeFilteredVerbatims.slice(
    (safeCurrentPage - 1) * safeVerbatimsPerPage, // Calculate the start index for the current page
    safeCurrentPage * safeVerbatimsPerPage // Calculate the end index for the current page
  );

  // Calculate the total number of pages based on the number of filtered verbatims and the number of verbatims per page
  const totalPages = Math.ceil(
    safeFilteredVerbatims.length / safeVerbatimsPerPage
  );

  /************************************************
   * VERBATIM MANAGEMENT FUNCTIONS
   ************************************************/

  // Handle editing a verbatim by ID
  const handleEditVerbatim = (id) => {
    const verbatim = storedVerbatims.find((v) => v.id === id); // Find the verbatim by its ID
    setVerbatimToEdit(verbatim); // Set the verbatim to edit
    navigate("/labeling"); // Navigate to the labeling page
  };

  // Handle deleting a specific verbatim by ID
  const handleDeleteVerbatim = (id) => {
    if (window.confirm("Are you sure you want to delete this verbatim?")) {
      // Ask for confirmation before deleting
      const updatedVerbatims = storedVerbatims.filter((v) => v.id !== id); // Filter out the verbatim with the given ID
      setVerbatims(updatedVerbatims); // Update the verbatims state
    }
  };

  // Handle deleting all stored verbatims
  const handleDeleteAllVerbatims = () => {
    if (
      window.confirm("Are you sure you want to delete all stored verbatims?")
    ) {
      // Ask for confirmation before deleting
      setVerbatims([]); // Clear all verbatims
    }
  };

  const handleCopyToClipboard = (id, verbatimContent, labels) => {
    let textToCopy = "";
  
    if (typeof verbatimContent === 'string') {
      textToCopy = `${verbatimContent} (${labels['Sources'][0]?.name ?? ''})`;
    } else if (Array.isArray(verbatimContent)) {
      const textParts = verbatimContent
        .filter(item => item.type === 'text')
        .map(item => item.content)
        .join('\n');
      
      textToCopy = `${textParts} (${labels['Sources'][0]?.name ?? ''})`;
    }
    
    // Copy the text to clipboard
    navigator.clipboard.writeText(textToCopy).then(
      () => {
        setBubbleState((prevState) => ({
          ...prevState,
          [id]: true, // Show bubble only for this row
        }));
        setTimeout(() => {
          setBubbleState((prevState) => ({
            ...prevState,
            [id]: false, // Hide bubble after 2 seconds
          }));
        }, 2000);
      },
      (err) => {
        console.error("Failed to copy: ", err);
      }
    );
  };

  // Function to copy the verbatim as it is displayed (useful when the page is translated with Google Translate)
  const handleCopyTranslatedToClipboard = (id, link) => {
    // Locate the <td> element by the data-verbatim-id attribute
    const translatedElement = document.querySelector(`td[data-verbatim-id="${id}"]`);
    if (translatedElement) {
      // Extract the translated text
      const translatedText = translatedElement.innerText || translatedElement.textContent;
      // Append the link in parentheses
      const formattedText = `${translatedText.trim()} (${link})`;

      // Copy the formatted text to clipboard
      navigator.clipboard.writeText(formattedText).then(
        () => {
          setTranslatedBubbleState((prevState) => ({
            ...prevState,
            [id]: true,
          }));
          setTimeout(() => {
            setTranslatedBubbleState((prevState) => ({
              ...prevState,
              [id]: false,
            }));
          }, 2000);
        },
        (err) => {
          console.error("Failed to copy translated text: ", err);
        }
      );
    } else {
      console.error("Translated content not found in the DOM.");
    }
  };

  const handleToggleUsed = (id) => {
    toggleUsedStatus(id); // Call the context function to toggle the "used" status
  };

  // New function to handle copying images to clipboard
  const handleCopyImage = async (base64Image) => {
    if (!base64Image) {
      console.error("Image not fournd.");
      return;
    }

    try {
      // Create a canvas element to draw the image
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Crate a new image element and set its source to the base64 image
      const img = new Image();
      img.src = base64Image;
      
      // Wait for the image to load
      await new Promise(resolve => { img.onload = resolve; });
      
      // Determine the canvas size based on the image dimensions
      canvas.width = img.width;
      canvas.height = img.height;
      
      // Draw the image on the canvas
      ctx.drawImage(img, 0, 0);
      
      // Convert the canvas to a blob
      const blob = await new Promise(resolve => canvas.toBlob(resolve));
      // Copy the blob to the clipboard
      if (navigator.clipboard && navigator.clipboard.write) {
        await navigator.clipboard.write([ new ClipboardItem({ [blob.type]: blob }) ]);
        setBubbleImageState(true);
        setTimeout(() => setBubbleImageState(false), 2000);
      } else {
        throw new Error("L'API Clipboard n'est pas disponible dans votre navigateur");
      }
    } catch (err) {
      console.error("Erreur lors de la copie de l'image:", err);
    }
  };

  /************************************************
   * MISC UI FUNCTIONS
   ************************************************/

  // Toggle the collapsible state (for showing/hiding a collapsible section in the UI)
  const toggleCollapsible = () => {
    setIsCollapsibleOpen(!isCollapsibleOpen); // Toggle the collapsible section's open/close state
  };

  /**
   * Function to render the verbatim content intelligently
   * this function checks if the content is a string or an array of objects
   * and renders it accordingly.
   */
  const renderVerbatimContent = (verbatim) => {
    if (!verbatim || !verbatim.verbatim) return null;

    const content = verbatim.verbatim;
    if (typeof content === 'string') {
      return (
        <>
          <p>{content}</p>
        </>
      );
    }

    if (Array.isArray(content)) {
      return (
        <div className="review-verbatim-content">
          {content.map((item, index) => {
            if (item.type === 'text' && item.content.trim() !== '') {
              return (
                <p key={index}>{item.content}</p>
              );
            } 
            else if (item.type === 'image' && item.content) {
              const imageUrl = base64ToImage(item.content);
              return (
                <div key={index} className="review-image-block">
                  <img 
                    src={imageUrl} 
                    alt="Verbatim" 
                    className="review-verbatim-image"
                    onClick={() => setModalImage(imageUrl)}
                    style={{ cursor: 'pointer' }}
                    onError={(e) => {
                      console.error("Erreur de chargement de l'image", e);
                      // Display a placeholder or error image if needed
                      e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiM5OTkiPkltYWdlIEVycm9yPC90ZXh0Pjwvc3ZnPg==";
                    }}
                  />
                </div>
              );
            }
            return null;
          })}
        </div>
      );
    }

    return null;
  };

  return (
    <div className="work-review-page">
      <div className="filter-bar-section">
        {/* Collapsible area on top of the FilterBar */}
        <div className="collapsible-container">
          <button className="collapsible-toggle" onClick={toggleCollapsible}>
            {/* Conditionally render the icons based on isCollapsibleOpen */}
            {isCollapsibleOpen ? (
              <i className="fa-solid fa-xmark menu-icon"></i>
            ) : (
              <i className="fa-solid fa-bars menu-icon"></i>
            )}
            {isCollapsibleOpen ? "Hide Menu" : "Show Menu"}
          </button>

          <div
            className="collapsible-content"
            style={{ display: isCollapsibleOpen ? "block" : "none" }}
          >
            <div className="pagination-settings">
              <label htmlFor="verbatims-per-page">Verbatims per page:</label>
              <select
                id="verbatims-per-page"
                value={verbatimsPerPage}
                onChange={handleVerbatimsPerPageChange}
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={500}>500</option>
                <option value={1000}>1000</option>
              </select>
            </div>

            <div className="defective-filter-toggle">
              <label>
                Show only verbatims to correct:
                <input
                  type="checkbox"
                  checked={showOnlyDefective}
                  onChange={(e) => {
                    const nextChecked = e.target.checked;
                    setShowOnlyDefective(nextChecked);

                    if (nextChecked) {
                      setResetFilters(true);
                    }
                  }}
                />
              </label>
            </div>

            <div className="file-actions">
              <PrimaryButton onClick={handleOpenModal}>
                <i className="fa-solid fa-download"></i> Export to Excel
              </PrimaryButton>
              <ExportVerbatimsModal
                show={isExportModalOpen}
                verbatims={storedVerbatims}
                setVerbatims={setVerbatims}
                onClose={handleCloseModal}
                onConfirm={handleExportToExcel}
              />
              <PrimaryButton onClick={() => setModalExportToTxt(true)} disabled={filteredVerbatims.length === 0}>
                <i className="fa-solid fa-download"></i> Export to .txt
              </PrimaryButton>
              <PrimaryButton>
                <i className="fa-solid fa-tasks"></i>
                <Link
                  to="/batch"
                  className={`${currentPage === "/batch" ? "active" : ""}`}
                  style={{ textDecoration: "none", color: "inherit" }} // Ensure consistent styling
                >
                  Edit in Batch
                </Link>
              </PrimaryButton>
              <div className="file-input">
                <input
                  type="file"
                  id="xlsx-file-input"
                  accept=".xlsx"
                  style={{ display: "none" }}
                  onChange={handleFileSelect}
                />
                <SecondaryButton
                  onClick={() =>
                    document.getElementById("xlsx-file-input").click()
                  }
                >
                  <i className="fa-solid fa-upload"></i> Load Verbatims (.xlsx
                  file)
                </SecondaryButton>
              </div>
              <div className="file-input">
                <input
                  type="file"
                  id="csv-file-input"
                  accept=".xlsx"
                  style={{ display: "none" }}
                  onChange={handleRadarlyExcelFileSelect}
                />
                <SecondaryButton
                  onClick={() =>
                    document.getElementById("csv-file-input").click()
                  }
                >
                  <i className="fa-solid fa-upload"></i> Load Radarly verbatims
                  (.xlsx file)
                </SecondaryButton>
              </div>
              <DeleteButton onClick={handleDeleteAllVerbatims}>
                <i className="fa-solid fa-trash"></i> Delete All Verbatims
              </DeleteButton>
            </div>
          </div>
        </div>
        <div className="verbatim-count">
          Showing {filteredVerbatims ? filteredVerbatims.length : 0} verbatims
        </div>
        {/* Search Bar for text-based search */}
        <div className="search-bar">
          <input
            type="text"
            className="search-input"
            id="verbatim-search"
            value={searchQuery}
            onChange={handleSearchQueryChange}
            placeholder="Search for a verbatim"
          />
        </div>
        <div className="filter-section">
          {/* Pass the handleFilterChange to the FilterBar */}
          <FilterBar
            onFilterChange={handleFilterChange}
            onResetFilters={() => setResetFilters(false)}
            reset={resetFilters}
          />
        </div>
      </div>

      <div className="verbatim-table-container">
        <table className="verbatim-table">
          <thead>
            <tr>
              <th className="column-large">Actions</th>{" "}
              {/* Actions column as the first column */}
              <th className="column-small defect-id-column">ID</th>{" "}
              <th className="column-small">O.ID</th>{" "}
              {/* ID column with small size */}
              <th className="column-very-large sticky-column">Verbatim</th>{" "}
              <th className="column-very-large">Translation</th>{" "}
              {labelTypes && Object.keys(labelTypes).length > 0
                ? Object.keys(labelTypes).map((labelType) => (
                  <th
                    key={labelType}
                    className={
                      ["Gender", "Age", "Sentiment"].includes(labelType)
                        ? "column-medium"
                        : "column-large"
                    }
                  >
                    {labelType}
                  </th>
                ))
                : null}
              <th className="column-medium">Links</th>{" "}
              {/* Source column with medium size */}
            </tr>
          </thead>
          <tbody>
            {paginatedVerbatims &&
              paginatedVerbatims.length > 0 &&
              paginatedVerbatims.map((verbatim) => (
                <tr key={verbatim.id}>
                  <td className="column-medium">
                    <IconButton
                      iconClass="fa fa-pencil"
                      onClick={() => handleEditVerbatim(verbatim.id)}
                      tooltip="Edit"
                    />
                    <IconButton
                      iconClass="fa fa-trash"
                      onClick={() => handleDeleteVerbatim(verbatim.id)}
                      tooltip="Delete"
                    />
                    <div className="copy-container">
                      <IconButton
                        iconClass="fa fa-copy"
                        onClick={() => handleCopyToClipboard(verbatim.id, verbatim.verbatim, verbatim.labels)}
                        tooltip="Copy original"
                      />
                      {bubbleState[verbatim.id] && <div className="copy-bubble">Copied Original!</div>}
                      <IconButton
                        iconClass="fa-regular fa-copy"
                        onClick={() => handleCopyTranslatedToClipboard(verbatim.id, extractDomainName(verbatim.link))}
                        tooltip="Copy displayed"
                      />
                      {translatedBubbleState[verbatim.id] && <div className="copy-bubble">Copied Translated!</div>}
                      {Array.isArray(verbatim.verbatim) && verbatim.verbatim.some(item => item.type === 'image') && (
                        <>
                          <IconButton
                            iconClass="fa-solid fa-file-image"
                            onClick={() => handleCopyImage(base64ToImage(verbatim.verbatim.find(item => item.type === 'image').content))}
                            tooltip="Copy image"
                          />
                          {bubbleImageState && <div className="copy-bubble">Copied image!</div>}
                        </>
                      )}
                    </div>
                    <IconButton
                      iconClass={`fa fa-paperclip ${verbatim.used ? 'used' : ''}`}
                      onClick={() => handleToggleUsed(verbatim.id)}
                      tooltip={verbatim.used ? "Unmark as Used" : "Mark as Used"}
                      className={verbatim.used ? 'used' : ''}
                    />
                  </td>
                  <td className="column-small defect-id-column">
                    <span className="verbatim-id-cell">
                      <span>{verbatim.id}</span>
                      {(() => {
                        const defectInfo = getVerbatimDefectInfo(verbatim);
                        return defectInfo.isDefective ? (
                          <i
                            className="fa-solid fa-circle-exclamation defective-verbatim-icon"
                            title={defectInfo.message || 'This verbatim needs correction.'}
                          ></i>
                        ) : null;
                      })()}
                    </span>
                  </td>
                  <td className="column-small">{verbatim?.original_id ?? ''}</td>
                  {/* ID column */}
                  <td className="column-very-large sticky-column" data-verbatim-id={verbatim.id}>
                    {renderVerbatimContent(verbatim)}
                  </td>{" "}
                  <td className="column-very-large">
                    {renderVerbatimTranslation(verbatim.translation)}
                  </td>
                  {/* Verbatim column */}
                  {labelTypes &&
                    Object.keys(labelTypes).length > 0 &&
                    Object.keys(labelTypes).map((labelType) => (
                      <td
                        key={labelType}
                        className={
                          ["Gender", "Age", "Sentiment"].includes(labelType)
                            ? "column-medium"
                            : "column-large"
                        }
                      >
                        {Array.isArray(verbatim.labels?.[labelType])
                          ? verbatim.labels[labelType]
                            .filter((l) => l && l.name) // Filter out any null or undefined labels
                            .map((l) => (
                              <LabelChip
                                key={l.name}
                                type={labelType} // Pass the label type (e.g., "Gender", "Age", "Sentiment")
                                name={l.name} // Pass the name of the label (e.g., "Woman", "25-34", "positive")
                                state={l.state} // Pass the label's state, if it exists
                              />
                            ))
                          : ""}
                      </td>
                    ))}
                  <td className="column-medium">
                    {verbatim.link ? (
                      <a
                        href={verbatim.link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {extractDomainName(verbatim.link)}
                      </a>
                    ) : (
                      ""
                    )}
                  </td>{" "}
                  {/* Source column */}
                </tr>
              ))}
          </tbody>
        </table>
        <div className="pagination-controls">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>
      <div>
        <LoadModal
          show={loadModalOpen}
          onClose={closeExcelImportFlow}
          onOptionSelect={handleLoadOptionSelect}
          validationReport={pendingExcelValidation}
          onContinueAll={handleValidationContinueAll}
          onContinueWithoutDefective={handleValidationContinueWithoutDefective}
          onRadarlyColumnsConfirm={handleRadarlyColumnsConfirm}
          radarlyColumns={csvColumns}
          loadMode={loadModalMode}
        />
      </div>
      {modalImage && (<ImageModal  imageUrl={modalImage} onClose={() => setModalImage(null)}/>)}
      {modalExportToTxt && <LabelTypeExportSelectorModal onClose={() => setModalExportToTxt(false)} onConfirm={(selectedLabelTypes) => handleExportToTxt(selectedLabelTypes)} />}
    </div>
  );
};

export default WorkReviewPage;
